if (!customElements.get('pc-product-configurator')) {
  class PcProductConfigurator extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('product-info');
      this.variant = JSON.parse(this.querySelector('script').textContent);
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      document.addEventListener('change', (event) => this.onChange(event), options);
      this.root.addEventListener('click', (event) => {
        if (event.target.closest('[data-pc-open-size]')) this.root.querySelector('[data-open-product-size-guide]')?.click();
        const colorCard = event.target.closest('[data-pc-option="color"] .m-product-option--node');
        if (colorCard && !event.target.closest('input,label,a,button')) {
          const input = colorCard.querySelector('input[type="radio"]');
          if (input && !input.disabled && !input.checked) input.click();
        }
      }, options);
      this.mediaQuery = window.matchMedia('(min-width: 768px)');
      this.mediaQuery.addEventListener('change', () => this.layout(), options);
      this.pickerObserver = new MutationObserver((records) => {
        const pickerChanged = records.some(record => [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 && (node.matches('variant-picker,[data-pc-option]') || node.querySelector('variant-picker,[data-pc-option]'))));
        if (!pickerChanged) return;
        cancelAnimationFrame(this.layoutFrame);
        this.layoutFrame = requestAnimationFrame(() => this.layout());
      });
      this.pickerObserver.observe(this.root, { childList: true, subtree: true });
      this.layout();
      this.initPackageSlider();
      const chest = this.root.querySelector('[data-pc-chest]');
      if (chest) {
        const fields = this.root.querySelector('[data-pc-personalization-fields]');
        const ready = fields?.querySelector('input[type="date"]') && fields?.querySelector('input:not([type="date"])');
        chest.querySelector('[data-pc-chest-choice="yes"]').disabled = !ready;
        chest.querySelector('[data-pc-chest-note]').hidden = !!ready;
      }
      document.addEventListener('variant:changed', (event) => {
        if (!this.root || !event.detail?.variant) return;
        this.variant = event.detail.variant;
        this.layout();
        this.update();
      }, options);
      this.update();
    }
    money(cents) {
      return new Intl.NumberFormat(document.documentElement.lang || 'ro', { style: 'currency', currency: this.dataset.currency || 'RON' }).format(cents / 100).replace('RON', 'lei');
    }
    initPackageSlider() {
      const container = this.root.querySelector('[data-pc-package-slider]');
      if (!container || !window.MinimogLibs?.Swiper) return;
      const block = container.closest('.pc-product__products');
      this.packageSlider = new MinimogLibs.Swiper(container, {
        slidesPerView: 2.2,
        spaceBetween: 10,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        breakpoints: { 768: { slidesPerView: 4 } },
        navigation: {
          prevEl: block.querySelector('[data-pc-package-prev]'),
          nextEl: block.querySelector('[data-pc-package-next]'),
        },
      });
      const syncControls = () => { block.querySelector('[data-pc-package-controls]').hidden = this.packageSlider.isLocked; };
      this.packageSlider.on('lock', syncControls);
      this.packageSlider.on('unlock', syncControls);
      syncControls();
    }
    layout() {
      const pickerBlock = this.root.querySelector('[data-pc-block="variant_picker"]');
      this.root.querySelectorAll('[data-pc-option]').forEach((option) => {
        option.style.order = option.dataset.pcOption === 'size' ? '50' : option.dataset.pcOption === 'color' ? '60' : (pickerBlock?.style.getPropertyValue('--pc-order') || '150');
      });
      const packageBlock = this.root.querySelector('[data-pc-block="package_contents"]');
      const packageContent = this.root.querySelector('[data-pc-package]');
      const slot = this.root.querySelector('[data-pc-package-slot]');
      if (packageContent && packageBlock && slot) {
        const target = this.mediaQuery.matches ? slot : packageBlock;
        if (packageContent.parentElement !== target) target.append(packageContent);
        this.packageSlider?.update();
      }
      const sizeLink = this.root.querySelector('[data-pc-open-size]');
      if (sizeLink) sizeLink.hidden = !this.root.querySelector('[data-open-product-size-guide]');
      // Variant swaps retain the old picker with display:none for 500 ms.
      // Count rendered steps only, including when an ancestor is CSS-hidden.
      const steps = [...this.root.querySelectorAll('[data-pc-step]')].filter(node => !node.closest('[hidden]') && node.getClientRects().length > 0);
      steps.sort((a,b) => {
        const order = node => Number(node.closest('[data-pc-option]')?.style.order || node.closest('[data-pc-block]')?.style.getPropertyValue('--pc-order') || 0);
        return order(a)-order(b);
      });
      steps.forEach((node,index) => { node.dataset.stepNumber = `${index+1}. `; });
    }
    onChange(event) {
      if (!this.root.contains(event.target) && !event.target.closest('sticky-atc')) return;
      if (event.target.matches('[data-pc-chest-choice="yes"]') && event.target.checked) {
        const custom = this.root.querySelector('[data-pc-personalization-choice="custom"]');
        if (custom && !custom.checked) {
          custom.checked = true;
          custom.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      if (event.target.matches('[data-pc-personalization-choice]')) {
        const fields = event.target.closest('[data-pc-personalization]').querySelector('[data-pc-personalization-fields]');
        fields.hidden = event.target.dataset.pcPersonalizationChoice !== 'custom';
        fields.querySelectorAll('input').forEach(input => { input.disabled = fields.hidden; });
        if (fields.hidden) {
          const noChest = this.root.querySelector('[data-pc-chest-choice="no"]');
          if (noChest) noChest.checked = true;
        }
        this.layout();
      }
      if (event.target.matches('[data-pc-accessory-variant]')) {
        const item = event.target.closest('[data-pc-accessory]');
        const selected = event.target.selectedOptions[0];
        const checkbox = item.querySelector('[data-product-order-addon]');
        checkbox.dataset.variantId = selected.value;
        checkbox.dataset.price = selected.dataset.price;
        item.querySelector('[data-pc-accessory-price]').textContent = this.money(Number(selected.dataset.price));
      }
      if (event.target.matches('[data-product-order-addon]')) {
        this.root.querySelectorAll('[data-product-order-addon]').forEach((input) => {
          if (input !== event.target && input.dataset.variantId === event.target.dataset.variantId) input.checked = event.target.checked;
        });
      }
      this.update();
    }
    update() {
      if (!this.variant) return;
      const selected = new Map([...this.root.querySelectorAll('[data-product-order-addon]:checked')].map((input) => [input.dataset.variantId, Number(input.dataset.price || 0) + Number(input.dataset.namePrice || 0)]));
      const unitTotal = this.variant.price + [...selected.values()].reduce((sum, price) => sum + price, 0);
      const mainQuantity = Math.max(1, Number(this.root.querySelector('.m-main-product--info [name="quantity"]')?.value || 1));
      this.root.querySelectorAll('[data-pc-summary-total]').forEach(node => { node.textContent = this.money(unitTotal * mainQuantity); });
      this.root.querySelectorAll('[data-pc-summary-label]').forEach(node => { node.textContent = selected.size ? 'Produs + opțiunile alese' : 'Produsul ales'; });
      document.body.classList.toggle('pc-product-has-addons', selected.size > 0);
      const buttons = [...this.root.querySelectorAll('button[name="add"]'), ...document.querySelectorAll('sticky-atc button[name="add"]')];
      buttons.forEach((button) => {
        if (button.closest('sticky-atc')) {
          button.querySelector('[data-pc-total]')?.remove();
          return;
        }
        let total = button.querySelector('[data-pc-total]');
        if (!total) {
          total = document.createElement('span');
          total.className = 'pc-product__total';
          total.dataset.pcTotal = '';
          button.append(total);
        }
        const quantity = Math.max(1, Number(button.form?.querySelector('[name="quantity"]')?.value || this.root.querySelector('[name="quantity"]')?.value || 1));
        total.hidden = !this.variant.available;
        total.textContent = ` — ${this.money(unitTotal * quantity)}`;
      });
      this.root.querySelectorAll('[data-pc-sku]').forEach((node) => { node.textContent = `SKU: ${this.variant.sku || ''}`; node.hidden = !this.variant.sku; });
      this.root.querySelectorAll('[data-pc-sale]').forEach((node) => {
        node.hidden = !(this.variant.compare_at_price > this.variant.price);
        if (!node.hidden) node.textContent = `−${Math.round((1 - this.variant.price / this.variant.compare_at_price) * 100)}%`;
      });
    }
    disconnectedCallback() { this.packageSlider?.destroy(true, true); this.abort?.abort(); this.pickerObserver?.disconnect(); cancelAnimationFrame(this.layoutFrame); document.body.classList.remove('pc-product-has-addons'); }
  }
  customElements.define('pc-product-configurator', PcProductConfigurator);
}
